import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  CloneEventDetails,
  Event,
  EventType,
  GoogleLocationData,
  NotificationType,
} from "@markit/common.types";
import { usePartialEventUpdates } from "./usePartialEventUpdates";
import {
  canPublishEvent,
  deepCopy,
  isExternalGenericLink,
  isExternalLinkNoData,
  isPaidTickets,
  validateEvent,
} from "@markit/common.utils";
import { difference } from "../utils/stringUtils";
import { makeEmptyEvent } from "../utils/makeEvent";
import { MixpanelContext } from "../context/AnalyticsService";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../redux/slices/accountSlice";
import { createEventData, updateEventData } from "../redux/slices/eventSlice";
import { addToCurrUserWishList } from "../redux/slices/wishMarkSlice";
import { DataLoaders } from "../redux/slices/dataSlice";
import { useNavigate } from "./useNavigate";
import { showNotificationBanner } from "../utils/notificationUtils";
import {
  NavigationId,
  NewProductNavigationId,
} from "../navigation/AppParamList";

type useCreateEventManagementProps = {
  isEditing: boolean;
  event?: Event;
  currentLocation?: GoogleLocationData;
  cloneDetails?: CloneEventDetails;
  resetStates?: () => void;
  setAlertText: (alertText: string) => void;
  type?: EventType; // For Form and Tracking Link creation/updates, if not defined, it's for events
};

export const useCreateEventManagement = (
  props: useCreateEventManagementProps
) => {
  const {
    event,
    isEditing,
    currentLocation,
    cloneDetails,
    resetStates,
    setAlertText,
    type,
  } = props;
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { account } = useSelector(getAccountState);
  const { accountData } = account;

  const [initialEventSettings, setInitialEventSettings] = useState<Event>(
    event
      ? deepCopy(event)
      : makeEmptyEvent(
          accountData.uid,
          accountData.isAdmin,
          currentLocation,
          type
        )
  );

  const { eventSettings, updateEventSettings, resetEventSettings } =
    usePartialEventUpdates({
      event,
      accountData,
      currentLocation,
      type,
    });

  const [postingEvent, setPostingEvent] = useState(false);
  const [showError, setShowError] = useState(false);
  const isPostingRef = useRef(false);

  const resetEvent = useCallback(() => {
    if (resetStates) {
      resetStates();
    }
    resetEventSettings();
  }, [resetEventSettings, resetStates]);

  const cleanEvent = useCallback(
    (tempEventSettings?: Event) => {
      const event = deepCopy(tempEventSettings || eventSettings);
      event.externalLink = event.externalLink.trim();
      return event;
    },
    [eventSettings]
  );

  const onFinalizeSaveEvent = useCallback(
    async (tempEventSettings?: Event) => {
      setPostingEvent(true);
      const event = cleanEvent(tempEventSettings);
      event.isDraft = false;

      try {
        const convertingFromDraft =
          initialEventSettings.isDraft && !event.isDraft;
        await dispatch(updateEventData(event, convertingFromDraft));

        // if converting a draft to a live event, call the event created mixpanel event from post event
        if (convertingFromDraft) {
          mixpanel.track("Event Created", {
            distinct_id: accountData.uid,
            event_id: event.id,
            event_location: event.googleDescription,
            event_is_paid: isPaidTickets(event),
            event_is_crowdfunded: event.crowdfundingGoal !== 0,
            event_is_university: event.universityId,
            event_host_name: accountData.fullName,
            event_host_username: accountData.username,
            event_host_phone_name: accountData.phoneNumber,
            event_host_email: accountData.email,
            event_partner: accountData.customer.phone !== "",
            num_event_texts: 2, // event texts removed from desktop creation so is always default 2
            creation_source: "webapp",
            type: eventSettings.eventType,
          });
          mixpanel.people_increment({ Events: 1 });
        }

        // get the difference in event object when event was edited, if any.
        const diff = difference(initialEventSettings, event);
        mixpanel.identify(accountData.uid);
        mixpanel.track("Event Updated", {
          distinct_id: accountData.uid,
          event_id: event.id,
          event_location: event.googleDescription,
          event_is_paid: isPaidTickets(event),
          event_is_crowdfunded: event.crowdfundingGoal !== 0,
          differences_in_event: diff,
          type: eventSettings.eventType,
        });
        showNotificationBanner(
          dispatch,
          "Updated!",
          NotificationType.AFFIRMATIVE
        );
        if (!isExternalLinkNoData(event.eventType)) {
          navigate(`/e/${event.id}/dashboard`, {
            state: { eventId: event.id },
          });
        }
        return true;
      } catch (error: any) {
        const { response } = error;
        if (response) {
          console.log(response.data);
        }
        setAlertText("Error Saving, please try again later");
        return false;
      } finally {
        setPostingEvent(false);
      }
    },
    [
      accountData.customer.phone,
      accountData.email,
      accountData.fullName,
      accountData.phoneNumber,
      accountData.uid,
      accountData.username,
      cleanEvent,
      dispatch,
      eventSettings.eventType,
      initialEventSettings,
      mixpanel,
      setAlertText,
      navigate,
    ]
  );

  // Returns false if we need to return early and end execution
  const onSaveEventPress = useCallback(
    async (tempEventSettings?: Event) => {
      if (isPostingRef.current) {
        return false;
      }
      isPostingRef.current = true;
      const finalEventSettings = tempEventSettings || eventSettings;
      try {
        if (
          !canPublishEvent(finalEventSettings) &&
          !isExternalGenericLink(finalEventSettings?.eventType)
        ) {
          setShowError(true);
          return false;
        }
        const validateEventMessage = validateEvent(finalEventSettings);
        if (validateEventMessage !== "") {
          setAlertText(validateEventMessage);
          return false;
        }
        return await onFinalizeSaveEvent(finalEventSettings);
      } finally {
        isPostingRef.current = false;
      }
    },
    [eventSettings, onFinalizeSaveEvent, setAlertText]
  );

  // Returns false if we need to return early and end execution
  const onPostEventPress = useCallback(
    async (tempEventSettings?: Event) => {
      if (isPostingRef.current) {
        return false;
      }
      isPostingRef.current = true;
      const finalEventSettings = tempEventSettings || eventSettings;
      try {
        if (
          !canPublishEvent(finalEventSettings) &&
          !isExternalGenericLink(finalEventSettings.eventType)
        ) {
          setShowError(true);
          return false;
        }
        const validateEventMessage = validateEvent(finalEventSettings);
        if (validateEventMessage !== "") {
          setAlertText(validateEventMessage);
          return false;
        }
        mixpanel.track("Completed Event Details Step", {
          distinct_id: accountData.uid,
          type: finalEventSettings.eventType,
        });
        setPostingEvent(true);

        const event = cleanEvent(finalEventSettings);
        const dispatchPromises = [
          dispatch(
            createEventData(
              event,
              mixpanel,
              !isExternalLinkNoData(event.eventType),
              cloneDetails
            )
          ),
          dispatch(addToCurrUserWishList(event.id, accountData.uid)),
        ];
        await Promise.all(dispatchPromises);

        showNotificationBanner(
          dispatch,
          "Created!",
          NotificationType.AFFIRMATIVE
        );
        if (!isExternalLinkNoData(event.eventType)) {
          navigate(`/e/${event.id}/dashboard`, {
            state: { eventId: event.id, fromCreate: true },
          });
        }
        return true;
      } catch (error: any) {
        const { response } = error;
        if (response) {
          console.log(response.data);
        }

        setAlertText("Error Creating, please try again later");
        if (mixpanel) {
          mixpanel.track("Failure Creating Event", {
            distinct_id: finalEventSettings.createdBy,
            event_id: finalEventSettings.id,
            error: error.message,
            type: finalEventSettings.eventType,
          });
        }
        return false;
      } finally {
        setPostingEvent(false);
        isPostingRef.current = false;
      }
    },
    [
      accountData.uid,
      cleanEvent,
      cloneDetails,
      dispatch,
      eventSettings,
      mixpanel,
      navigate,
      setAlertText,
    ]
  );

  const onDraftEventPress = useCallback(
    async (newDraft: boolean) => {
      try {
        const event = deepCopy(eventSettings);
        event.isDraft = true;

        if (newDraft) {
          const dispatchPromises = [
            dispatch(createEventData(event, mixpanel, false, cloneDetails)),
            dispatch(addToCurrUserWishList(event.id, accountData.uid)),
          ];
          await Promise.all(dispatchPromises);
        } else {
          await dispatch(updateEventData(event));
        }
        if (mixpanel) {
          mixpanel.track("Event Draft Created", {
            distinct_id: accountData.uid,
            event_id: event.id,
            draft_type: newDraft ? "created" : "updated",
            type: eventSettings.eventType,
          });
        }
        showNotificationBanner(
          dispatch,
          "Draft Created!",
          NotificationType.AFFIRMATIVE
        );

        if (accountData.inCreatorMode) {
          navigate(NavigationId.HOME_PRODUCTS);
        } else {
          dispatch(DataLoaders.user(accountData.uid));
          navigate(`/u/${accountData.username}`, {
            state: { username: accountData.username },
          });
        }
      } catch (error: any) {
        const { response } = error;
        if (response) {
          console.log(response.data);
        }

        if (newDraft) {
          setAlertText("Error drafting event, please try again later");
          if (mixpanel) {
            mixpanel.track("Failure Creating Event", {
              distinct_id: eventSettings.createdBy,
              event_id: eventSettings.id,
              error: error.message,
              is_draft: true,
              type: eventSettings.eventType,
            });
          }
        }
      }
    },
    [
      accountData.inCreatorMode,
      accountData.uid,
      accountData.username,
      cloneDetails,
      dispatch,
      eventSettings,
      mixpanel,
      navigate,
      setAlertText,
    ]
  );

  const onNextExternalPress = useCallback(() => {
    if (!canPublishEvent(eventSettings)) {
      setShowError(true);
      return;
    }
    const validateMessage = validateEvent(eventSettings);
    if (validateMessage) {
      setAlertText(validateMessage);
      return;
    }
    navigate(
      isEditing
        ? `/e/${eventSettings.id}/editLink`
        : NewProductNavigationId.LINK_EVENT,
      {
        state: { confirmState: true, event: eventSettings },
      }
    );
  }, [eventSettings, isEditing, navigate, setAlertText]);

  const setRouteParams = useCallback(() => {
    const eventSettingsUpdates: Partial<Event> = {};

    updateEventSettings({ ...event, ...eventSettingsUpdates });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, isEditing]);

  useEffect(() => setRouteParams(), [setRouteParams]);

  // When editing from a direct URL, `isEditing` can be true before `event` loads.
  // Refresh this baseline when `event` changes so draft->publish detection stays accurate.
  useEffect(
    () =>
      setInitialEventSettings(
        event
          ? deepCopy(event)
          : makeEmptyEvent(
              accountData.uid,
              accountData.isAdmin,
              currentLocation,
              type
            )
      ),
    [accountData.isAdmin, accountData.uid, currentLocation, event, type]
  );

  return {
    updateEventSettings,
    resetEvent,
    postingEvent,
    isEditing,
    showError,
    setShowError,
    eventSettings,
    initialEventSettings,
    onPostEventPress,
    onSaveEventPress,
    onNextExternalPress,
    onDraftEventPress,
  };
};
